import { useEffect, useState } from 'preact/hooks';
import { Pill, Connection, ErrorBox } from '../components/ui.tsx';
import { Result } from '../components/Tally.tsx';
import { api, ApiError } from '../lib/api.ts';
import { CHOICE, votingPill } from '../lib/labels.ts';
import { subscribe, type ConnectionState } from '../lib/realtime.ts';
import type { Choice, EventStatus, VotingView } from '../lib/types.ts';

type Me = { eventId: string; memberId: string; displayName: string };

export function Legislador() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      // El QR impreso abre /legislador?qr=<token>: se canjea y se quita de la URL.
      const qr = new URLSearchParams(location.search).get('qr');
      if (qr) {
        history.replaceState(null, '', '/legislador');
        try {
          await api('POST', '/api/event-auth/qr', { token: qr });
        } catch (e) {
          setError((e as Error).message);
        }
      }
      try {
        setMe((await api<{ session: Me }>('GET', '/api/event-auth/me')).session);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  if (me === undefined) return <div class="app narrow"><p class="hint">Cargando…</p></div>;
  if (me === null) return <PinLogin initialError={error} onLogin={setMe} />;
  return <Ballot me={me} onLogout={() => setMe(null)} />;
}

function PinLogin({ initialError, onLogin }: { initialError: string | null; onLogin: (me: Me) => void }) {
  const [events, setEvents] = useState<Array<{ id: string; title: string; status: EventStatus }>>([]);
  const [eventId, setEventId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(initialError);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ events: typeof events }>('GET', '/api/event-auth/events').then(({ events }) => {
      setEvents(events);
      const live = events.find((e) => e.status === 'in_session') ?? events[0];
      if (live) setEventId(live.id);
    }).catch((e: Error) => setError(e.message));
  }, []);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin((await api<{ session: Me }>('POST', '/api/event-auth/pin', { eventId, pin })).session);
    } catch (err) {
      setError((err as Error).message);
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="app narrow">
      <header class="top"><div><h1>Votar</h1><p class="sub">Escanea tu código QR o escribe el PIN que te dio la mesa.</p></div></header>
      <form class="panel" onSubmit={submit}>
        <ErrorBox error={error} />
        {events.length === 0 && !error && <p class="notice">No hay sesiones programadas ni en curso.</p>}
        <div class="field">
          <label for="evento">Sesión</label>
          <select id="evento" value={eventId} onChange={(e) => setEventId(e.currentTarget.value)} required>
            {events.map((e) => <option key={e.id} value={e.id}>{e.title}{e.status === 'in_session' ? ' (en sesión)' : ''}</option>)}
          </select>
        </div>
        <div class="field">
          <label for="pin">PIN</label>
          <input id="pin" class="pin" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
            value={pin} onInput={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))} required />
        </div>
        <button class="btn primary" type="submit" disabled={busy || pin.length !== 6 || !eventId}>{busy ? 'Entrando…' : 'Entrar'}</button>
      </form>
    </div>
  );
}

function Ballot({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [voting, setVoting] = useState<VotingView | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [picked, setPicked] = useState<Choice | null>(null);
  const [myChoice, setMyChoice] = useState<Choice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sync = async () => {
      try {
        const { voting: v } = await api<{ voting: VotingView | null }>('GET', '/api/my/voting');
        // Sin votación abierta se conserva el resultado de la última hasta que abra otra.
        setVoting((prev) => v ?? (prev && prev.state !== 'voting' ? prev : null));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) onLogout();
      }
    };
    return subscribe(me.eventId, {
      onSync: () => void sync(),
      onState: setConnection,
      onMessage: (m) => {
        if (m.type === 'voting') {
          setVoting((prev) => ({
            ...m.voting, result: m.voting.result as VotingView['result'],
            hasVoted: prev?.id === m.voting.id ? Boolean(prev.hasVoted) : false,
          }));
          if (m.voting.state === 'voting') setError(null);
        } else if (m.type === 'ballot') {
          setVoting((prev) => (prev?.id === m.votingId && m.ballotsCast >= prev.ballotsCast ? { ...prev, ballotsCast: m.ballotsCast, tally: m.tally } : prev));
        }
      },
    });
  }, [me.eventId]);

  // Una votación nueva limpia la selección local.
  useEffect(() => {
    setPicked(null);
    setMyChoice(null);
  }, [voting?.id]);

  const cast = async () => {
    if (!voting || !picked) return;
    setBusy(true);
    setError(null);
    try {
      await api('POST', `/api/votings/${voting.id}/ballots`, { choice: picked });
      setMyChoice(picked);
      // Actualización funcional: el aviso en vivo de este mismo voto puede haber llegado antes.
      setVoting((v) => (v ? { ...v, hasVoted: true } : v));
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && e.code === 'already_voted') setVoting((v) => (v ? { ...v, hasVoted: true } : v));
    } finally {
      setBusy(false);
      setPicked(null);
    }
  };

  const logout = async () => {
    await api('POST', '/api/event-auth/logout').catch(() => {});
    onLogout();
  };

  const pill = votingPill(voting);
  return (
    <div class="app narrow">
      <header class="top">
        <div><h1>Hola, {me.displayName}</h1><div class="row" style={{ marginTop: '8px' }}><Connection state={connection} /></div></div>
        <button class="btn link" onClick={logout}>Salir</button>
      </header>
      <section class="panel" aria-live="polite">
        {!voting && <p>No hay una votación abierta. Esta pantalla se actualiza sola.</p>}
        {voting && (
          <>
            <Pill kind={pill.kind}>{pill.text}</Pill>
            <h3>{voting.matter.title}{voting.round > 1 ? ` (ronda ${voting.round})` : ''}</h3>
            <p class="hint">
              {voting.mode === 'secreta' ? 'Votación secreta: nadie podrá ver qué votaste.' : 'Votación nominal: tu voto será público.'}
              {' '}Han votado {voting.ballotsCast} de {voting.totalMembers} integrantes.
            </p>
            <ErrorBox error={error} />
            {voting.state === 'voting' && !voting.hasVoted && !picked && (
              <div class="vote-big">
                {(['for', 'against', 'abstain'] as const).map((c) => (
                  <button key={c} class={`btn vote ${c}`} onClick={() => setPicked(c)}><span>{CHOICE[c]}</span></button>
                ))}
              </div>
            )}
            {voting.state === 'voting' && !voting.hasVoted && picked && (
              <div class="block">
                <p>¿Confirmas tu voto <b>{CHOICE[picked].toLowerCase()}</b>? Ya no se puede cambiar.</p>
                <div class="vote-big">
                  <button class={`btn vote ${picked} picked`} onClick={cast} disabled={busy}><span>{busy ? 'Enviando…' : `Confirmar: ${CHOICE[picked]}`}</span></button>
                  <button class="btn" onClick={() => setPicked(null)} disabled={busy}>Elegir otra opción</button>
                </div>
              </div>
            )}
            {voting.hasVoted && voting.state === 'voting' && (
              <p class="notice">Tu voto quedó registrado{myChoice ? `: ${CHOICE[myChoice].toLowerCase()}` : ''}.</p>
            )}
            {voting.state === 'closing' && <p class="notice">Hubo empate. La presidencia decide con su voto de calidad.</p>}
            {voting.state === 'closed' && <Result voting={voting} />}
          </>
        )}
      </section>
    </div>
  );
}
